/**
 * Centralized localStorage service for the AWatch application
 * Provides type-safe, organized access to browser storage with error handling
 */

import { createErrorLogger } from '../utils/errorHandler';

const logger = createErrorLogger('storageService');

const FIELD_LABEL_OVERRIDES = {
  PolicyViolation: 'Violation Of Policy',
  Subjects: 'Key Narratives',
};

/**
 * Storage keys used throughout the application
 */
export const STORAGE_KEYS = {
  ENTITY_CONFIG: {
    DATA: 'awatch_entity_configuration',
    TIMESTAMP: 'awatch_entity_configuration_timestamp',
  },
};

/**
 * Storage service class with error handling and type conversion
 */
class StorageService {
  getItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      if (item === null || item === 'undefined' || item === 'null') {
        return defaultValue;
      }
      return JSON.parse(item);
    } catch (error) {
      logger.error(`Failed to get item "${key}" from localStorage`, error);
      return defaultValue;
    }
  }

  getRawItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item === null ? defaultValue : item;
    } catch (error) {
      logger.error(`Failed to get raw item "${key}" from localStorage`, error);
      return defaultValue;
    }
  }

  setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      logger.error(`Failed to set item "${key}" in localStorage`, error);
      return false;
    }
  }

  setRawItem(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      logger.error(`Failed to set raw item "${key}" in localStorage`, error);
      return false;
    }
  }

  // ==================== Cache Methods ====================

  getCachedData(dataKey, timestampKey, maxAge) {
    const data = this.getItem(dataKey);
    const timestamp = this.getRawItem(timestampKey);

    if (!timestamp || !data) {
      return { data: null, isValid: false };
    }

    try {
      const lastUpdate = new Date(timestamp).getTime();
      const isValid = Date.now() - lastUpdate < maxAge;
      return { data, isValid };
    } catch (error) {
      logger.error('Failed to validate cache timestamp', error);
      return { data, isValid: false };
    }
  }

  setCachedData(dataKey, timestampKey, data) {
    this.setItem(dataKey, data);
    this.setRawItem(timestampKey, new Date().toISOString());
  }

  // ==================== Entity Configuration Methods ====================

  getEntityConfiguration() {
    return this.getItem(STORAGE_KEYS.ENTITY_CONFIG.DATA);
  }

  setEntityConfiguration(config) {
    this.setItem(STORAGE_KEYS.ENTITY_CONFIG.DATA, config);
    this.setRawItem(STORAGE_KEYS.ENTITY_CONFIG.TIMESTAMP, new Date().toISOString());
  }

  async fetchAndUpdateEntityConfiguration() {
    try {
      const { getEntityConfiguration } = await import('../api/entities_api');
      const config = await getEntityConfiguration();
      if (config?.fieldLabels) {
        Object.assign(config.fieldLabels, FIELD_LABEL_OVERRIDES);
      }
      this.setEntityConfiguration(config);
      return config;
    } catch (error) {
      logger.error('Failed to fetch entity configuration', error);
      throw error;
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();
